import React, { useState } from 'react';
import {
	Alert,
	Box,
	Button,
	Chip,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	FormHelperText,
	InputLabel,
	MenuItem,
	Paper,
	Select,
	Skeleton,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TextField,
	Tooltip,
	Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import { shallowEqual, useSelector } from 'react-redux';
import { RootState } from 'store';
import { SectionInfoTooltip } from 'features/salesDashboard/components/SectionInfoTooltip';
import { CachedBadge } from 'features/salesDashboard/components/CachedBadge';
import { useCashBalance } from '../../hooks/useCashBalance';
import { useCashMovements } from '../../hooks/useCashMovements';
import { postCashMovement, postCashExchange, extractApiError } from '../../services';
import {
	CashBox,
	ICashBalance,
	CashMovementType,
	CashMovementCategory,
	CASH_MOVEMENT_CATEGORY_LABELS,
	CASH_MOVEMENT_CATEGORY_HELP,
	EGRESO_CATEGORIES,
	INGRESO_CATEGORIES,
	FUERTE_EGRESO_CATEGORIES,
	FUERTE_INGRESO_CATEGORIES,
} from '../../types';
import { BRANCH_CURRENCIES } from '../../constants';
import { formatCashAmount } from '../BranchCashSummary';

// ─── Tooltips ─────────────────────────────────────────────────────────────────

const cajaChicaInfoTooltip = (
	<>
		<Typography variant="body2" fontWeight={600} gutterBottom>
			¿Qué es la caja chica? 💰
		</Typography>
		<Typography variant="body2" gutterBottom>
			Es el efectivo que la sucursal guarda para operaciones del día a día: dar vuelto a clientes, cubrir gastos
			menores, etc.
		</Typography>
		<Typography variant="body2" gutterBottom>
			Cuando se hace un depósito bancario, el monto de los recibos que no fue al banco queda registrado acá como{' '}
			<em>saldo disponible</em>.
		</Typography>
		<Typography variant="body2">
			Cada movimiento queda registrado con quién lo hizo y cuándo, para que siempre haya trazabilidad.
		</Typography>
	</>
);

const cajaFuerteInfoTooltip = (
	<>
		<Typography variant="body2" fontWeight={600} gutterBottom>
			¿Qué es la caja fuerte? 🔐
		</Typography>
		<Typography variant="body2" gutterBottom>
			Acá se guardan los billetes que el banco no aceptó. Cuando un acta de depósito se hace con destino{' '}
			<strong>caja fuerte</strong>, ese efectivo entra acá automáticamente al confirmarla.
		</Typography>
		<Typography variant="body2">
			Cuando alguien retira los billetes para llevarlos al depósito especial, se registra un egreso{' '}
			<em>Retiro para depósito especial</em>.
		</Typography>
	</>
);

// ─── Tarjeta de balance por moneda ───────────────────────────────────────────

function BalanceCard({ balance }: { balance: ICashBalance }): JSX.Element {
	const hasTarget = balance.target !== null;
	const hasExcess = hasTarget && balance.excess !== null && balance.excess > 0;

	return (
		<Paper
			variant="outlined"
			sx={{
				p: 2,
				minWidth: 180,
				borderColor: hasExcess ? 'warning.main' : 'divider',
				bgcolor: hasExcess ? 'warning.50' : 'background.paper',
			}}
		>
			<Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
				{balance.currency}
			</Typography>
			<Typography variant="h5" fontWeight={700}>
				{new Intl.NumberFormat('es-AR', { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(balance.balance)}
			</Typography>
			{hasTarget && (
				<Typography variant="caption" color="text.secondary">
					Objetivo: {balance.target !== null ? new Intl.NumberFormat('es-AR').format(balance.target) : ''}
					{hasExcess && balance.excess !== null && (
						<Typography component="span" variant="caption" color="warning.main" fontWeight={600}>
							{' '}
							(exceso: {new Intl.NumberFormat('es-AR').format(balance.excess)})
						</Typography>
					)}
				</Typography>
			)}
			<Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.5 }}>
				{balance.movements} mov.
			</Typography>
		</Paper>
	);
}

// Categorías EGRESO que implican una orden de venta (muestran el campo)
const CATEGORIES_WITH_SALE_ORDER: CashMovementCategory[] = ['VUELTO', 'PAGO_PROVEEDOR'];

// ─── Modal para registrar un movimiento ─────────────────────────────────────

interface RegisterMovementModalProps {
	open: boolean;
	onClose: () => void;
	branch: string;
	box: CashBox;
	onSuccess: () => void;
}

function RegisterMovementModal({ open, onClose, branch, box, onSuccess }: RegisterMovementModalProps): JSX.Element {
	const user = useSelector((s: RootState) => s.auth.user, shallowEqual);
	const isVault = box === 'FUERTE';

	// Las monedas se derivan del branch, no del balance (funciona incluso cuando la caja está vacía)
	const currencies = BRANCH_CURRENCIES[branch] ?? ['USD'];

	const defaultEgresoCategory: CashMovementCategory = isVault ? 'RETIRO_CAJA_FUERTE' : 'GASTO';
	const defaultIngresoCategory: CashMovementCategory = 'AJUSTE';

	const [currency, setCurrency] = useState(currencies[0]);
	const [type, setType] = useState<CashMovementType>('EGRESO');
	const [category, setCategory] = useState<CashMovementCategory>(defaultEgresoCategory);
	const [amount, setAmount] = useState('');
	const [amountFocused, setAmountFocused] = useState(false);
	const [note, setNote] = useState('');
	const [saleOrderId, setSaleOrderId] = useState('');
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const availableCategories = isVault
		? type === 'EGRESO'
			? FUERTE_EGRESO_CATEGORIES
			: FUERTE_INGRESO_CATEGORIES
		: type === 'EGRESO'
		? EGRESO_CATEGORIES
		: INGRESO_CATEGORIES;
	const showSaleOrder = !isVault && CATEGORIES_WITH_SALE_ORDER.includes(category);

	const handleTypeChange = (newType: CashMovementType) => {
		setType(newType);
		// Reset categoría al cambiar tipo para evitar combinaciones inválidas
		setCategory(newType === 'EGRESO' ? defaultEgresoCategory : defaultIngresoCategory);
		setSaleOrderId('');
	};

	// Parsea el monto desde su representación actual (raw o formateada con es-AR)
	const parseAmountNum = (): number => parseFloat(amount.replace(/\./g, '').replace(',', '.')) || 0;

	// Al perder foco: formatea con separador de miles (1200000 → 1.200.000)
	const handleAmountBlur = () => {
		setAmountFocused(false);
		const n = parseAmountNum();
		if (n > 0) setAmount(new Intl.NumberFormat('es-AR', { maximumFractionDigits: 2 }).format(n));
	};

	// Al recibir foco: muestra el número puro para facilitar la edición
	const handleAmountFocus = () => {
		setAmountFocused(true);
		const n = parseAmountNum();
		if (n > 0) setAmount(String(n));
	};

	const reset = () => {
		setAmount('');
		setNote('');
		setSaleOrderId('');
		setError(null);
		setType('EGRESO');
		setCategory(defaultEgresoCategory);
		setCurrency(currencies[0]);
	};

	const handleClose = () => {
		if (loading) return;
		reset();
		onClose();
	};

	const handleSubmit = async () => {
		const numAmount = parseAmountNum();
		if (!numAmount || numAmount <= 0) {
			setError('El monto debe ser mayor a 0');
			return;
		}
		if (!note.trim() && category === 'OTRO') {
			setError('Para la categoría "Otro" la nota es obligatoria — explicá de qué se trata');
			return;
		}

		setLoading(true);
		setError(null);
		try {
			await postCashMovement({
				branch,
				box,
				currency,
				type,
				category,
				amount: numAmount,
				note: note.trim() || undefined,
				saleOrderId: showSaleOrder && saleOrderId.trim() ? saleOrderId.trim() : undefined,
				registeredBy: user?.email ?? 'unknown',
			});
			onSuccess();
			reset();
			onClose();
		} catch (err) {
			setError(extractApiError(err));
		} finally {
			setLoading(false);
		}
	};

	return (
		<Dialog open={open} onClose={handleClose} fullWidth maxWidth="xs">
			<DialogTitle>
				{isVault ? 'Registrar movimiento de caja fuerte' : 'Registrar movimiento de caja chica'}
			</DialogTitle>
			<DialogContent>
				<Stack spacing={2} mt={0.5}>
					{/* Tipo: siempre primero para condicionar todo lo demás */}
					<FormControl size="small" fullWidth>
						<InputLabel>Tipo de movimiento</InputLabel>
						<Select
							value={type}
							label="Tipo de movimiento"
							onChange={(e) => handleTypeChange(e.target.value as CashMovementType)}
						>
							<MenuItem value="EGRESO">
								<Box>
									<Typography variant="body2" fontWeight={600}>
										Egreso — sale dinero
									</Typography>
									<Typography variant="caption" color="text.secondary">
										{isVault
											? 'Retiro de billetes para el depósito especial'
											: 'Vuelto a clientes, gastos, pagos a proveedores'}
									</Typography>
								</Box>
							</MenuItem>
							<MenuItem value="INGRESO">
								<Box>
									<Typography variant="body2" fontWeight={600}>
										Ingreso — entra dinero
									</Typography>
									<Typography variant="caption" color="text.secondary">
										{isVault
											? 'Ajuste de diferencia al arquear la caja fuerte'
											: 'Reposición de caja, ajuste de diferencia positiva'}
									</Typography>
								</Box>
							</MenuItem>
						</Select>
					</FormControl>

					{/* Categoría: opciones cambian según el tipo */}
					<FormControl size="small" fullWidth>
						<InputLabel>Categoría</InputLabel>
						<Select
							value={category}
							label="Categoría"
							onChange={(e) => {
								setCategory(e.target.value as CashMovementCategory);
								setSaleOrderId(''); // limpiar orden si cambia la categoría
							}}
						>
							{availableCategories.map((c) => (
								<MenuItem key={c} value={c}>
									{CASH_MOVEMENT_CATEGORY_LABELS[c]}
								</MenuItem>
							))}
						</Select>
						{/* Helper text explica cuándo usar esta categoría */}
						<FormHelperText>{CASH_MOVEMENT_CATEGORY_HELP[category]}</FormHelperText>
					</FormControl>

					{/* Moneda: siempre visible, selector si hay más de una */}
					{currencies.length > 1 ? (
						<FormControl size="small" fullWidth>
							<InputLabel>Moneda</InputLabel>
							<Select value={currency} label="Moneda" onChange={(e) => setCurrency(e.target.value)}>
								{currencies.map((c) => (
									<MenuItem key={c} value={c}>
										{c}
									</MenuItem>
								))}
							</Select>
						</FormControl>
					) : (
						<Box
							sx={{
								px: 1.5,
								py: 1,
								borderRadius: 1,
								bgcolor: 'grey.50',
								border: 1,
								borderColor: 'divider',
								display: 'flex',
								alignItems: 'center',
								gap: 1,
							}}
						>
							<Typography variant="caption" color="text.secondary">
								Moneda:
							</Typography>
							<Typography variant="body2" fontWeight={600}>
								{currencies[0]}
							</Typography>
						</Box>
					)}

					{/* Monto */}
					<TextField
						label="Monto"
						size="small"
						fullWidth
						value={amount}
						onChange={(e) => setAmount(e.target.value)}
						onFocus={handleAmountFocus}
						onBlur={handleAmountBlur}
						placeholder={amountFocused ? '0' : '0'}
						helperText={!amountFocused && amount ? undefined : 'Ingresá el número sin separadores'}
						InputProps={{
							endAdornment: (
								<Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
									{currency}
								</Typography>
							),
						}}
					/>

					{/* Nota: obligatoria para OTRO */}
					<TextField
						label={category === 'OTRO' ? 'Nota (obligatoria)' : 'Nota'}
						size="small"
						fullWidth
						required={category === 'OTRO'}
						value={note}
						onChange={(e) => setNote(e.target.value)}
						placeholder={
							category === 'OTRO' ? 'Describí de qué se trata este movimiento' : 'Opcional — cualquier aclaración útil'
						}
						inputProps={{ maxLength: 255 }}
						helperText={`${note.length}/255`}
					/>

					{/* Orden de venta: solo para VUELTO y PAGO_PROVEEDOR */}
					{showSaleOrder && (
						<TextField
							label="ID de orden de venta"
							size="small"
							fullWidth
							value={saleOrderId}
							onChange={(e) => setSaleOrderId(e.target.value)}
							placeholder="Opcional — vincula el movimiento a una OV"
							helperText={
								category === 'VUELTO'
									? 'Si el vuelto fue por una transacción en una orden específica, pegá el ID acá.'
									: 'Si el pago corresponde a una orden de venta, pegá el ID acá.'
							}
						/>
					)}

					{error && <Alert severity="error">{error}</Alert>}
				</Stack>
			</DialogContent>
			<DialogActions sx={{ px: 3, pb: 2 }}>
				<Button onClick={handleClose} disabled={loading} sx={{ textTransform: 'none' }}>
					Cancelar
				</Button>
				<Button onClick={handleSubmit} variant="contained" disabled={loading} sx={{ textTransform: 'none' }}>
					{loading ? <CircularProgress size={18} /> : 'Registrar'}
				</Button>
			</DialogActions>
		</Dialog>
	);
}

// ─── Modal de cambio de moneda ───────────────────────────────────────────────

interface ExchangeModalProps {
	open: boolean;
	onClose: () => void;
	branch: string;
	onSuccess: () => void;
}

/**
 * Cambio de moneda dentro de la caja chica: registra el PAR de movimientos
 * (sale una moneda, entra su equivalente en otra). El caso típico: diste
 * vuelto en una moneda distinta a la del pago — primero registrás el cambio
 * acá y después el vuelto en la moneda correcta.
 */
function ExchangeModal({ open, onClose, branch, onSuccess }: ExchangeModalProps): JSX.Element {
	const user = useSelector((s: RootState) => s.auth.user, shallowEqual);
	const currencies = BRANCH_CURRENCIES[branch] ?? ['USD'];

	const [fromCurrency, setFromCurrency] = useState(currencies[0]);
	const [toCurrency, setToCurrency] = useState(currencies[1] ?? currencies[0]);
	const [fromAmount, setFromAmount] = useState('');
	const [toAmount, setToAmount] = useState('');
	const [note, setNote] = useState('');
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const parse = (s: string): number => parseFloat(s.replace(/\./g, '').replace(',', '.')) || 0;

	const reset = () => {
		setFromAmount('');
		setToAmount('');
		setNote('');
		setError(null);
		setFromCurrency(currencies[0]);
		setToCurrency(currencies[1] ?? currencies[0]);
	};

	const handleClose = () => {
		if (loading) return;
		reset();
		onClose();
	};

	const handleSubmit = async () => {
		const fromNum = parse(fromAmount);
		const toNum = parse(toAmount);
		if (fromCurrency === toCurrency) {
			setError('Elegí dos monedas distintas');
			return;
		}
		if (fromNum <= 0 || toNum <= 0) {
			setError('Los dos montos deben ser mayores a 0');
			return;
		}
		setLoading(true);
		setError(null);
		try {
			await postCashExchange({
				branch,
				fromCurrency,
				fromAmount: fromNum,
				toCurrency,
				toAmount: toNum,
				note: note.trim() || undefined,
				registeredBy: user?.email ?? 'unknown',
			});
			onSuccess();
			reset();
			onClose();
		} catch (err) {
			setError(extractApiError(err));
		} finally {
			setLoading(false);
		}
	};

	return (
		<Dialog open={open} onClose={handleClose} fullWidth maxWidth="xs">
			<DialogTitle>Cambio de moneda en caja chica</DialogTitle>
			<DialogContent>
				<Stack spacing={2} mt={0.5}>
					<Alert severity="info" sx={{ py: 0.5 }}>
						Registra el par: <strong>sale</strong> una moneda de la caja y <strong>entra</strong> su equivalente en
						otra. Si fue por un vuelto, registrá después el vuelto en la moneda correcta.
					</Alert>
					<Box display="flex" gap={1}>
						<FormControl size="small" sx={{ minWidth: 90 }}>
							<InputLabel>Sale</InputLabel>
							<Select value={fromCurrency} label="Sale" onChange={(e) => setFromCurrency(e.target.value)}>
								{currencies.map((c) => (
									<MenuItem key={c} value={c}>
										{c}
									</MenuItem>
								))}
							</Select>
						</FormControl>
						<TextField
							label="Monto que sale"
							size="small"
							fullWidth
							value={fromAmount}
							onChange={(e) => setFromAmount(e.target.value)}
							placeholder="0"
						/>
					</Box>
					<Box display="flex" gap={1}>
						<FormControl size="small" sx={{ minWidth: 90 }}>
							<InputLabel>Entra</InputLabel>
							<Select value={toCurrency} label="Entra" onChange={(e) => setToCurrency(e.target.value)}>
								{currencies.map((c) => (
									<MenuItem key={c} value={c}>
										{c}
									</MenuItem>
								))}
							</Select>
						</FormControl>
						<TextField
							label="Monto que entra"
							size="small"
							fullWidth
							value={toAmount}
							onChange={(e) => setToAmount(e.target.value)}
							placeholder="0"
						/>
					</Box>
					<TextField
						label="Nota"
						size="small"
						fullWidth
						value={note}
						onChange={(e) => setNote(e.target.value)}
						placeholder="Opcional — ej: cambio para vuelto de la OV 1234"
						inputProps={{ maxLength: 255 }}
					/>
					{error && <Alert severity="error">{error}</Alert>}
				</Stack>
			</DialogContent>
			<DialogActions sx={{ px: 3, pb: 2 }}>
				<Button onClick={handleClose} disabled={loading} sx={{ textTransform: 'none' }}>
					Cancelar
				</Button>
				<Button onClick={handleSubmit} variant="contained" disabled={loading} sx={{ textTransform: 'none' }}>
					{loading ? <CircularProgress size={18} /> : 'Registrar cambio'}
				</Button>
			</DialogActions>
		</Dialog>
	);
}

// ─── Panel principal ──────────────────────────────────────────────────────────

function formatDateTime(isoString: string): string {
	return new Date(isoString).toLocaleString('es', {
		day: '2-digit',
		month: '2-digit',
		year: '2-digit',
		hour: '2-digit',
		minute: '2-digit',
	});
}

interface CashChicaPanelProps {
	branch: string;
	/** Caja física a mostrar: CHICA (default) o FUERTE (caja fuerte). */
	box?: CashBox;
}

/**
 * Panel de caja chica o caja fuerte (`box`): saldos por moneda + libro de
 * movimientos con filtros. Permite registrar movimientos manuales.
 */
export function CashChicaPanel({ branch, box = 'CHICA' }: CashChicaPanelProps): JSX.Element {
	const [modalOpen, setModalOpen] = useState(false);
	const [exchangeOpen, setExchangeOpen] = useState(false);
	const [filterCurrency, setFilterCurrency] = useState<string | undefined>(undefined);
	const isVault = box === 'FUERTE';
	const canExchange = !isVault && (BRANCH_CURRENCIES[branch]?.length ?? 0) > 1;

	const {
		data: balanceData,
		loading: balanceLoading,
		error: balanceError,
		refetch: refetchBalance,
	} = useCashBalance(branch, box);
	const {
		movements,
		pagination,
		loading: movLoading,
		loadingMore,
		error: movError,
		cachedAt,
		loadMore,
		hasMore,
		refetch: refetchMovements,
	} = useCashMovements(branch, { box, currency: filterCurrency }, 30);

	const currencies = balanceData?.balances.map((b) => b.currency) ?? [];

	const handleMovementSuccess = () => {
		refetchBalance();
		refetchMovements();
	};

	return (
		<>
			{/* ── Saldos ─────────────────────────────────────── */}
			<Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
				<Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
					<Box display="inline-flex" alignItems="center" gap={0.5}>
						<Typography variant="subtitle1" fontWeight={600}>
							{isVault ? 'Saldo de caja fuerte' : 'Saldo de caja chica'}
						</Typography>
						<SectionInfoTooltip content={isVault ? cajaFuerteInfoTooltip : cajaChicaInfoTooltip} />
					</Box>
					<Box display="flex" gap={1}>
						{canExchange && (
							<Button
								size="small"
								variant="outlined"
								startIcon={<SwapHorizIcon sx={{ fontSize: 15 }} />}
								onClick={() => setExchangeOpen(true)}
								sx={{ textTransform: 'none' }}
							>
								Cambio de moneda
							</Button>
						)}
						<Button
							size="small"
							variant="outlined"
							startIcon={<AddIcon sx={{ fontSize: 15 }} />}
							onClick={() => setModalOpen(true)}
							sx={{ textTransform: 'none' }}
						>
							Registrar movimiento
						</Button>
					</Box>
				</Box>

				{balanceLoading ? (
					<Skeleton variant="rectangular" height={80} />
				) : balanceError ? (
					<Alert severity="error">{balanceError}</Alert>
				) : balanceData?.balances.length === 0 ? (
					<Box sx={{ py: 1 }}>
						<Typography variant="body2" color="text.secondary" gutterBottom>
							{isVault
								? 'Todavía no hay movimientos en la caja fuerte de esta sucursal.'
								: 'Todavía no hay movimientos en la caja chica de esta sucursal.'}
						</Typography>
						<Typography variant="caption" color="text.disabled">
							{isVault
								? 'Cuando se confirme un acta de depósito con destino caja fuerte, el efectivo va a entrar acá automáticamente. También podés registrar un movimiento manual con el botón de arriba.'
								: 'Cuando se confirme el primer acta de depósito, el sistema va a crear automáticamente los saldos iniciales. También podés registrar un movimiento manual ahora usando el botón de arriba.'}
						</Typography>
					</Box>
				) : (
					<Stack direction="row" flexWrap="wrap" sx={{ gap: 2 }}>
						{balanceData?.balances.map((b) => <BalanceCard key={b.currency} balance={b} />)}
					</Stack>
				)}
			</Paper>

			{/* ── Libro de movimientos ─────────────────────── */}
			<Box display="flex" justifyContent="space-between" alignItems="center" mb={1} flexWrap="wrap" gap={1}>
				<Typography variant="subtitle2" fontWeight={600}>
					Libro de movimientos
				</Typography>
				<Box display="flex" alignItems="center" gap={1.5}>
					<CachedBadge cachedAt={cachedAt} />
					{currencies.length > 1 && (
						<Stack direction="row" spacing={0.5}>
							<Chip
								label="Todas"
								size="small"
								variant={filterCurrency === undefined ? 'filled' : 'outlined'}
								onClick={() => setFilterCurrency(undefined)}
								sx={{ cursor: 'pointer' }}
							/>
							{currencies.map((c) => (
								<Chip
									key={c}
									label={c}
									size="small"
									variant={filterCurrency === c ? 'filled' : 'outlined'}
									onClick={() => setFilterCurrency(c)}
									sx={{ cursor: 'pointer' }}
								/>
							))}
						</Stack>
					)}
				</Box>
			</Box>

			{movLoading ? (
				<Skeleton variant="rectangular" height={200} />
			) : movError ? (
				<Alert severity="error">{movError}</Alert>
			) : movements.length === 0 ? (
				<Typography variant="body2" color="text.secondary">
					No hay movimientos todavía.
				</Typography>
			) : (
				<>
					<TableContainer component={Paper} variant="outlined" sx={{ mb: 1, maxHeight: 500, overflow: 'auto' }}>
						<Table size="small" stickyHeader>
							<TableHead>
								<TableRow>
									<TableCell>Fecha</TableCell>
									<TableCell>Tipo</TableCell>
									<TableCell>Categoría</TableCell>
									<TableCell align="right">Monto</TableCell>
									<TableCell>Nota</TableCell>
									<TableCell>Registrado por</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{movements.map((mov) => {
									const isContra = mov.reversesMovementId !== null;
									const isEgreso = mov.type === 'EGRESO';
									return (
										<TableRow key={mov.id} hover sx={{ opacity: isContra ? 0.65 : 1 }}>
											<TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDateTime(mov.createdAt)}</TableCell>
											<TableCell>
												<Chip
													label={isEgreso ? 'Egreso' : 'Ingreso'}
													size="small"
													color={isEgreso ? 'error' : 'success'}
													variant="outlined"
													sx={{ fontSize: 11 }}
												/>
												{isContra && (
													<Tooltip title={`Contra-movimiento del mov. #${mov.reversesMovementId}`}>
														<SwapHorizIcon
															sx={{ fontSize: 14, ml: 0.5, color: 'text.secondary', verticalAlign: 'middle' }}
														/>
													</Tooltip>
												)}
											</TableCell>
											<TableCell>{CASH_MOVEMENT_CATEGORY_LABELS[mov.category] ?? mov.category}</TableCell>
											<TableCell align="right">
												<Typography variant="body2" fontWeight={600} color={isEgreso ? 'error.main' : 'success.main'}>
													{isEgreso ? '−' : '+'}
													{formatCashAmount(mov.amount, mov.currency)}
												</Typography>
											</TableCell>
											<TableCell sx={{ maxWidth: 200 }}>
												<Typography variant="body2" color="text.secondary" noWrap title={mov.note ?? ''}>
													{mov.note ?? '—'}
												</Typography>
											</TableCell>
											<TableCell>{mov.registeredBy}</TableCell>
										</TableRow>
									);
								})}
							</TableBody>
						</Table>
					</TableContainer>
					{hasMore && (
						<Box display="flex" justifyContent="center" mb={2}>
							<Chip
								label={loadingMore ? 'Cargando…' : `Cargar más (${movements.length} de ${pagination?.total ?? 0})`}
								onClick={loadMore}
								variant="outlined"
								size="small"
								sx={{ cursor: 'pointer' }}
							/>
						</Box>
					)}
				</>
			)}

			{/* ── Modales ────────────────────────────────────── */}
			<RegisterMovementModal
				open={modalOpen}
				onClose={() => setModalOpen(false)}
				branch={branch}
				box={box}
				onSuccess={handleMovementSuccess}
			/>
			{canExchange && (
				<ExchangeModal
					open={exchangeOpen}
					onClose={() => setExchangeOpen(false)}
					branch={branch}
					onSuccess={handleMovementSuccess}
				/>
			)}
		</>
	);
}
